import { Directive, ElementRef, EventEmitter, HostListener, inject, Output } from '@angular/core';

import { getValidationConfigFromCardNo, CardBrandEnum } from '../../validation';

/**
 * Card number mask directive that provides format-as-you-type behavior.
 * Automatically detects card brand and applies appropriate mask pattern:
 * - Amex (34, 37): 4-6-5 (0000 000000 00000)
 * - Diners (30, 36, 38, 39): 4-6-4 (0000 000000 0000)
 * - Default: 4-4-4-4-3 (0000 0000 0000 0000 000) for up to 19 digits
 *
 * Usage:
 * ```html
 * <input appCardNumberMask [(ngModel)]="cardNumber" (ngModelChange)="onChange($event)" />
 * ```
 *
 * Note: The ngModel will contain the formatted value with spaces.
 * Strip non-digits in your change handler if you need raw digits.
 */
@Directive({
  selector: '[appCardNumberMask]',
  standalone: true,
  host: {
    inputmode: 'numeric',
    autocomplete: 'cc-number',
  },
})
export class CardNumberMaskDirective {
  private readonly _el = inject(ElementRef<HTMLInputElement>);

  /**
   * Emits the detected card brand when it changes.
   * Emits null when no brand is detected (partial or unknown number).
   */
  @Output() cardBrandDetected = new EventEmitter<CardBrandEnum | null>();

  /**
   * Track last emitted brand to avoid duplicate emissions.
   */
  private _lastEmittedBrand: CardBrandEnum | null = null;

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;

    // Extract only digits
    const rawDigits = oldValue.replace(/\D/g, '');

    // Format based on card brand
    const formatted = this.formatCardNumber(rawDigits);

    // Only update if the formatted value is different from current
    if (input.value !== formatted) {
      // Calculate new cursor position before updating value
      const newCursorPosition = this.calculateCursorPosition(oldValue, formatted, cursorPosition);

      // Update input value with formatted version
      input.value = formatted;

      // Restore cursor position
      requestAnimationFrame(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      });
    }

    // Detect and emit card brand (only if changed to avoid flicker)
    const cardConfig = getValidationConfigFromCardNo(rawDigits);
    const newBrand = cardConfig?.type ?? null;

    if (newBrand !== this._lastEmittedBrand) {
      this._lastEmittedBrand = newBrand;
      this.cardBrandDetected.emit(newBrand);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    const isCtrlCmd = event.ctrlKey || event.metaKey;
    const allowedWithCtrl = ['a', 'c', 'v', 'x'];
    // Allow: home, end, left, right arrows
    const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (
      allowedKeys.includes(event.key) ||
      navigationKeys.includes(event.key) ||
      (isCtrlCmd && allowedWithCtrl.includes(event.key.toLowerCase()))
    ) {
      return;
    }

    // Allow numbers only
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  private formatCardNumber(digits: string): string {
    const cardConfig = getValidationConfigFromCardNo(digits);

    if (cardConfig?.type === CardBrandEnum.AMERICANEXPRESS) {
      // Amex: 4-6-5 = 15 digits
      return this.formatWithPattern(digits, [4, 6, 5]);
    }

    if (cardConfig?.type === CardBrandEnum.DINERSCLUB) {
      // Diners: 4-6-4 = 14 digits
      return this.formatWithPattern(digits, [4, 6, 4]);
    }

    // Default: 4-4-4-4-3 = 19 digits max
    return this.formatWithPattern(digits, [4, 4, 4, 4, 3]);
  }

  private formatWithPattern(digits: string, groups: number[]): string {
    const result: string[] = [];
    let currentIndex = 0;

    for (const groupSize of groups) {
      if (currentIndex >= digits.length) break;
      const group = digits.slice(currentIndex, currentIndex + groupSize);
      result.push(group);
      currentIndex += groupSize;
    }

    return result.join(' ');
  }

  private calculateCursorPosition(oldValue: string, newValue: string, oldPosition: number): number {
    // Count digits before cursor in old value
    const digitsBeforeCursor = oldValue.slice(0, oldPosition).replace(/\D/g, '').length;

    // Find position in new value that has the same number of digits before it
    let digitCount = 0;
    for (let i = 0; i < newValue.length; i++) {
      if (/\d/.test(newValue[i])) {
        digitCount++;
      }
      if (digitCount === digitsBeforeCursor) {
        // Position after the last matched digit
        return i + 1;
      }
    }

    return newValue.length;
  }
}
